// client/src/helpers/errorDispatch.ts
//
// Code-keyed notification helper used by every Redux thunk catch block.
//
// Two error shapes reach it:
//   1. `ApiError` — thrown by the axios response interceptor for every
//      non-401 failure. Carries `code`, `status` and typed `details`.
//   2. A raw axios-like object `{ response: { data: { code } } }` — only
//      from code paths that bypass the interceptor (tests, direct axios).
//
// Lookup order: errors:{code} (with formatted details) → the api's message →
// errors:GENERIC_ERROR → the caller-supplied fallback.
import { notification } from "antd";
import i18n from "../i18n";
import { ApiError, errorTranslationKey, translateApiError } from "../i18n/errorCodes";

const FINAL_FALLBACK = "We couldn't complete that. Please try again.";

export function dispatchErrorNotification(
  err: unknown,
  fallback?: string,
): void {
  if (err instanceof ApiError) {
    notification.error({ message: translateApiError(err) });
    return;
  }

  const anyErr = err as {
    response?: { data?: { code?: unknown } };
  };
  const rawCode = anyErr?.response?.data?.code;
  const code: string =
    typeof rawCode === "string" && rawCode.length > 0 ? rawCode : "GENERIC_ERROR";

  const message = i18n.t(errorTranslationKey(code), {
    defaultValue: i18n.t("errors:GENERIC_ERROR", {
      defaultValue: fallback ?? FINAL_FALLBACK,
    }),
  });

  notification.error({ message: String(message) });
}

export default dispatchErrorNotification;
